/**
 * The health contract — requirement 4.7, and the one payload in the system whose HTTP status is
 * the message.
 *
 * `/api/health` exists for two readers: the platform, which only looks at the status code and
 * restarts or drains the instance on a failure, and a human, who needs to know *which* dependency
 * is down without reading a log. So the body is a small report and the status is derived from it:
 * `200` while the database answers, `503` when it does not. A route that returned `200` with
 * `database: "unreachable"` would keep a broken instance in rotation, which is the failure this
 * endpoint is supposed to prevent.
 *
 * Deliberately not the error envelope. A 503 here is not a failed request — the request succeeded
 * and the answer is bad news. Wrapping it in `ApiError` would throw away the only field the reader
 * came for, and the envelope's `code` cannot say which dependency is down.
 *
 * Deliberately absent: anything about the environment. No hostname, no connection string, no
 * variable names, no dependency versions. This endpoint is unauthenticated on both surfaces
 * because a platform health check cannot log in, so everything in it is public.
 */
import { z } from 'zod';
/**
 * Service status. Two values, because the platform's decision is binary — a third state would
 * have to map onto one of these anyway, and whichever way it mapped would surprise someone.
 */
export const healthStatuses = ['ok', 'unavailable'];
/** Dependency reachability, named as the question it answers rather than as a boolean. */
export const dependencyStates = ['reachable', 'unreachable'];
export const healthReportSchema = z.object({
    status: z.enum(healthStatuses),
    /** The result of a real query, not of holding a connection object. */
    database: z.enum(dependencyStates),
    /** The running build. Mirrors `apps/api`'s manifest version; the API asserts they agree. */
    version: z.string().min(1),
    /**
     * Whole seconds since this process started. Integer on purpose: a fractional uptime invites
     * a reader to compare two responses to the millisecond, which says nothing about health.
     */
    uptimeSeconds: z.number().int().nonnegative(),
    /** Aggregate-only operational signal; no request identifiers or customer data are exposed. */
    support: z.object({ interruptedSideEffects: z.number().int().nonnegative() }).strict().optional(),
});
/**
 * The transport status for a report, so the route cannot decide it differently from the contract.
 * `503` is what makes a platform health check fail (requirement 4.7).
 */
export const statusForHealthReport = (report) => report.status === 'ok' ? 200 : 503;
//# sourceMappingURL=health.js.map